import React from 'react'
import { PublicationCardItem, CardEvents } from '@news-mono/web-common'
import { Lima } from '../../collections/Lima/Lima'
import { CollectionEvent } from '@news-mono/web-common'
import { themedValue } from '../../__styling/themed-value'
import { useTheme } from '@emotion/react'
import { LayoutName } from '../../cards/Portrait/Portrait.layouts'
import { LandscapeLayout } from '../../cards/Landscape/Landscape.layouts'
import { RenderTargetContext } from '@news-mono/web-common'
import { StyledAside } from './InlineRelatedContent.styled'
import { ResponsivePictureSizes } from '../Picture/responsive'

export interface InlinerelatedContentProps {
    items: PublicationCardItem[]
    onEvent: (event: CardEvents | CollectionEvent) => void
}

export const InlineRelatedContent: React.FC<InlinerelatedContentProps> = ({
    items,
    onEvent,
}) => {
    const renderTarget = React.useContext(RenderTargetContext)

    const theme = useTheme()
    if (items.length === 0 || renderTarget.renderTarget === 'rss') {
        return null
    }

    const inlineContentSection =
        theme.section === 'community-news'
            ? ('inline-content-cng' as const)
            : ('inline-content' as const)

    const sectionHeadingPerthNow =
        theme.section === 'community-news'
            ? 'More from Community News'
            : 'More from PerthNow'

    const sectionHeader = themedValue(theme, {
        sevennews: { heading: 'More from 7NEWS.com.au', fontScale: 1 as const },
        thewest: { heading: 'More from The West', fontScale: 1 as const },
        perthnow: {
            heading: sectionHeadingPerthNow,
            fontScale: 1 as const,
            section: inlineContentSection,
        },
    })

    const landscapeCardLayout: LandscapeLayout = themedValue(theme, {
        thewest: {
            type: 'landscape',
            format: 'landscape-xsNoTeaser-mdTeaser-5050',
        },
        sevennews: {
            type: 'landscape',
            format: 'landscape-xsNoTeaser-mdTeaser-5050-simple',
        },
        perthnow: {
            type: 'landscape',
            format: 'landscape-inline-content-perthnow',
        },
    })

    const portraitCardLayout: LayoutName = 'noTeaserNoPadding1xFont'

    const imageWidths = themedValue(theme, {
        thewest: {
            mobile: '110px',
            tablet: '369px',
            desktop: '315px',
            fallbackWidth: 315,
            mode: 'fixed',
        },
        perthnow: {
            mobile: '218px',
            tablet: '475px',
            desktop: '263px',
            fallbackWidth: 263,
            mode: 'fixed',
        },
        sevennews: {
            mobile: '215px',
            tablet: '345px',
            desktop: '275px',
            fallbackWidth: 275,
            mode: 'fixed',
        },
    }) as ResponsivePictureSizes

    if (items.length === 1) {
        return (
            <StyledAside>
                <Lima
                    onEvent={onEvent}
                    expectedCards={1}
                    initialColumns={1}
                    intermediateColumns={1}
                    finalColumns={1}
                    hideByline={true}
                    hideBorder={true}
                    verticalSpacing={'lg'}
                    fixedRatios={['16:9']}
                    sectionHeader={sectionHeader}
                    cardLayout={[[landscapeCardLayout]]}
                    items={{ loaded: true, result: items }}
                    imageWidths={imageWidths}
                />
            </StyledAside>
        )
    }

    return (
        <StyledAside>
            <Lima
                onEvent={onEvent}
                expectedCards={2}
                initialColumns={2}
                intermediateColumns={2}
                finalColumns={2}
                kickerMode="hidden"
                verticalSpacing={'lg'}
                fixedRatios={['16:9']}
                sectionHeader={sectionHeader}
                cardLayout={[[portraitCardLayout], [portraitCardLayout]]}
                items={{ loaded: true, result: items.slice(0, 2) }}
            />
        </StyledAside>
    )
}
InlineRelatedContent.displayName = 'InlineRelatedContent'
